// src/hooks/useCategory.ts
import { useCategoriesStore, useItemsStore } from "../store";
import { useMenuActions } from "./useMenuActions";


interface UseCategoryProps {
  category: { id: string; name: string };
}

export const useCategory = ({ category }: UseCategoryProps) => {
  const { selectCategory } = useMenuActions();
  const setCategorySelected = useCategoriesStore((s) => s.setCategorySelected);
  const setIsManualScrolling = useCategoriesStore((s) => s.setIsManualScrolling);

  const isHome = category.id === "home-link";

  const isSelected = useCategoriesStore((s) => {
    const selectedId = s.categorySelected?.id;
    if (isHome) return !selectedId;
    return selectedId === category.id;
  });

  const handleAction = () => {
    if (isHome) {
      setIsManualScrolling(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setCategorySelected(null);
      setTimeout(() => setIsManualScrolling(false), 600);
      return;
    }

    const target = document.getElementById(`cat-${category.id}`);
    if (!target) return;

    setIsManualScrolling(true);
    setCategorySelected(category);

    // Offset: CSS var del navbar + altura real de la barra de categorías.
    // Usa offsetHeight para no depender de la posición de scroll actual.
    const categoriesBar = document.querySelector<HTMLElement>('[data-sticky-categories-bar]');
    const navbarH = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--navbar-height')
    ) || 80;
    const stickyOffset = navbarH + (categoriesBar?.offsetHeight ?? 48);

    const elementTop = target.getBoundingClientRect().top + window.scrollY;
    const offset = elementTop - stickyOffset;

    window.scrollTo({ top: offset, behavior: "smooth" });

    // Guard: scrollend libera exactamente al terminar; timeout es fallback para Safari
    // y para scrolls muy cortos donde scrollend puede no disparar.
    let guardTimeout: ReturnType<typeof setTimeout>;
    const releaseGuard = () => {
      clearTimeout(guardTimeout);
      setIsManualScrolling(false);
    };
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', releaseGuard, { once: true });
    }
    guardTimeout = setTimeout(releaseGuard, 1200);
  };

  return { isHome, isSelected, handleAction };
};
